import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, Eye, EyeOff, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "../lib/UserAuthContext";
import { LOGO_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rules = [
  { id: "len", label: "At least 8 characters", test: (v) => v.length >= 8 },
  { id: "special", label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const passOk = rules.every((r) => r.test(form.password));
  const matches = form.password.length > 0 && form.password === form.confirm;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!passOk) { toast.error("Please satisfy the password requirements."); return; }
    if (!matches) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      const email = form.email.trim().toLowerCase();
      const { data } = await axios.post(`${API}/auth/register-request-otp`, {
        email,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      // Cache pending signup email for the OTP page
      sessionStorage.setItem("cashpark_pending_email", email);
      if (data?.dev_code) {
        // Fallback path when Resend can't deliver (unverified domain/testing mode)
        sessionStorage.setItem("cashpark_dev_code", data.dev_code);
        toast.warning("Email service is in test mode — your code is on the next screen.", { duration: 5000 });
      } else {
        toast.success(`We sent a 6-digit code to ${email}.`);
      }
      nav("/verify-otp", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-8" data-testid="register-brand-link">
          <img src={LOGO_URL} alt="CashPark" className="h-14 w-14" />
          <span className="font-display font-bold text-xl text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Inc</span></span>
        </Link>
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm" data-testid="register-form">
          <h1 className="font-display text-2xl font-bold text-[#0B192C]">Create your account</h1>
          <p className="text-sm text-slate-500 mt-1">We'll email you a 6-digit code to confirm.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name">First name</Label>
              <div className="relative mt-1.5">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="first_name" name="first_name" required value={form.first_name} onChange={onChange} data-testid="register-first-name" className="pl-9 h-11 rounded-xl" />
              </div>
            </div>
            <div>
              <Label htmlFor="last_name">Last name</Label>
              <div className="relative mt-1.5">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="last_name" name="last_name" required value={form.last_name} onChange={onChange} data-testid="register-last-name" className="pl-9 h-11 rounded-xl" />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={onChange} data-testid="register-email" className="pl-9 h-11 rounded-xl" />
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="phone">Phone</Label>
            <div className="relative mt-1.5">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="phone" name="phone" autoComplete="tel" required value={form.phone} onChange={onChange} placeholder="+1 403 …" data-testid="register-phone" className="pl-9 h-11 rounded-xl" />
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="password" name="password" type={showPw ? "text" : "password"} autoComplete="new-password" required value={form.password} onChange={onChange} data-testid="register-password" className="pl-9 pr-10 h-11 rounded-xl" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="password-rules">
              {rules.map((r) => {
                const ok = r.test(form.password);
                return (
                  <li key={r.id} className={`text-xs inline-flex items-center gap-1.5 ${ok ? "text-emerald-600" : "text-slate-400"}`} data-testid={`pw-rule-${r.id}`}>
                    {ok ? <Check size={12} /> : <X size={12} />} {r.label}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="mt-3">
            <Label htmlFor="confirm">Confirm password</Label>
            <div className="relative mt-1.5">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="confirm" name="confirm" type={showPw ? "text" : "password"} autoComplete="new-password" required value={form.confirm} onChange={onChange} data-testid="register-confirm" className={`pl-9 pr-10 h-11 rounded-xl ${form.confirm && !matches ? "border-red-300 focus-visible:ring-red-100" : ""}`} />
            </div>
            {form.confirm && !matches && (
              <p className="text-xs text-red-600 mt-1.5" data-testid="password-mismatch">Passwords do not match.</p>
            )}
          </div>

          <Button type="submit" disabled={busy} data-testid="register-submit" className="mt-6 w-full h-11 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
            {busy ? "Sending code…" : "Send verification code"}
          </Button>

          <p className="mt-5 text-sm text-slate-500 text-center">
            Already have an account? <Link to="/login" className="text-[#FF6B00] font-semibold" data-testid="register-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
